import io from 'socket.io-client';
import { reactive, watch, toRefs } from 'vue';
import { useAuthStore } from '/src/store/stores/useAuthStore.js';
import { getToken } from '/src/apis/sockets.js';

const { token, streamSubdomain } = useAuthStore();

const debug = import.meta.env.DEV;
const defaultOptions = {
	autoConnect: false,
	upgrade: true,
	transports: ['polling', 'websocket'],
};

const state = reactive({
	connected: false,
	identified: false,
});

let socket;

export function useSocket () {
	if (socket) {
		return define();
	}

	createSocket();

	watch(() => streamSubdomain.value, (stream) => {
		socket.io.uri = `wss://${streamSubdomain.value}.${import.meta.env.VITE_APP_DOMAIN}`;
	});

	function createSocket () {
		const url = `wss://${streamSubdomain.value}.${import.meta.env.VITE_APP_DOMAIN}`;
		socket = io(url, defaultOptions);

		const onevent = socket.onevent;
		socket.onevent = function (packet) {
			const args = packet.data || [];
			onevent.call(this, packet);

			packet.data = ['*'].concat(args);
			onevent.call(this, packet);
		};

		socket.onerror = function (error) {
			console.log('socket error: ', error);
		};

		if (debug) {
			socket.on('*', function (event, data) {
				console.log(`[Socket: ${event}]: `, data);
			});
		}
	}

	function connect () {
		socket.connect();
		listen();
	}

	function disconnect () {
		socket.disconnect();

		unlisten();
	}

	async function identify () {
		if (!token.value) return;

		const streamToken = await getToken();
		socket.emit('identity', JSON.stringify({ token: streamToken }));
	}

	function listen () {
		socket.on('connect', onSocketConnected);
		socket.on('identified', onSocketIdentified);
		socket.on('disconnect', onSocketDisconnected);
	}

	function unlisten () {
		socket.off('connect', onSocketConnected);
		socket.off('identified', onSocketIdentified);
		socket.off('disconnect', onSocketDisconnected);
	}

	function onSocketConnected () {
		console.log('[Socket]: connected');
		state.connected = true;

		identify();
	}

	function onSocketIdentified () {
		state.identified = true;
	}

	function onSocketDisconnected () {
		console.log('[Socket]: disconnected');
		state.connected = false;
		state.identified = false;
	}

	function define () {
		return {
			socket,
			connect,
			disconnect,
			...toRefs(state),
		};
	}

	return define();
}
